/**
 * Save a file the browser cannot simply link to.
 *
 * A plain `<a href>` carries no `Authorization` header and no `X-Share-Password`, and
 * both of the file endpoints in the product need one or the other -- reports are not
 * public objects (CLAUDE.md 15.3), and a shared one may sit behind a password (11.4).
 * So the bytes are fetched, turned into a blob URL, and the browser's own save dialog
 * does the rest.
 */
import { tokens } from '@/api/client'

export interface DownloadOptions {
  /** Extra headers, e.g. `X-Share-Password` on a protected share link. */
  headers?: Record<string, string>
  /** Send the bearer token. Off for the public share endpoints. */
  auth?: boolean
}

/** Fetch a protected file as the signed-in user. `null` on any failure. */
export async function fetchFile(
  path: string,
  options: DownloadOptions & { signal?: AbortSignal } = {},
): Promise<Blob | null> {
  const { headers = {}, auth = true, signal } = options
  try {
    const response = await fetch(path, {
      signal,
      headers: {
        ...headers,
        ...(auth && tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {}),
      },
    })
    if (!response.ok) return null
    return await response.blob()
  } catch {
    return null
  }
}

export async function downloadFile(path: string, options: DownloadOptions = {}): Promise<boolean> {
  const { headers = {}, auth = true } = options

  const response = await fetch(path, {
    headers: {
      ...headers,
      ...(auth && tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {}),
    },
  })
  if (!response.ok) return false

  // The server sets the engineering filename (P01_BEAM_B1.pdf); honour it.
  const disposition = response.headers.get('content-disposition') ?? ''
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
  const filename = match?.[1] ? decodeURIComponent(match[1]) : 'download'

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  // Revoke on the next tick: revoking synchronously can cancel the download in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
  return true
}
